import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { Close } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { GridMenuIcon } from '@mui/x-data-grid';
import ListItemButton from '@mui/material/ListItemButton';
import { Button, Dialog, Typography, SwipeableDrawer } from '@mui/material';

import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useAuth } from 'src/hooks/use-auth';
import { useUser } from 'src/hooks/use-user';
import { useResponsive } from 'src/hooks/use-responsive';

import Logo from 'src/components/logo';
import SvgColor from 'src/components/svg-color';
import Scrollbar from 'src/components/scrollbar';

import { NAV } from './config-layout';

// ----------------------------------------------------------------------

type NavItemData = {
  permission: string;
  title: string;
  icon: React.ReactNode;
  path?: string;
  action?: () => void;
  condition?: boolean;
};

type NavProps = {
  openNav: boolean;
  setOpenNav: (open: boolean) => void;
};

export default function Nav({ openNav, setOpenNav }: NavProps) {
  const { user } = useUser();
  const { setAuth } = useAuth();
  const [logout, setLogout] = useState(false);

  const handleCloseLogout = () => {
    setLogout(false);
  };

  const upLg = useResponsive('up', 'xl');
  const icon = (name: string) => <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />;
  const navConfig = (
    [
      {
        permission: 'patient',
        title: 'nav.home',
        path: '/app',
        icon: icon('ic_home'),
      },
      {
        permission: '-',
        title: 'nav.feedback',
        path: '/feedback',
        icon: icon('ic_feedback'),
      },
      {
        permission: 'patient',
        title: 'nav.logout',
        action: () => setLogout(true),
        icon: icon('ic_logout'),
      },
      {
        permission: 'patient',
        title: 'nav.privacy_policy',
        action: () => window.open('https://megi.ai/us/privacy-policy/', '_blank'),
        icon: icon('ic_privacy_policy'),
      },
      {
        permission: '-',
        title: 'nav.unsubscribe',
        path: '/unsubscribe',
        icon: icon('ic_unsubscribe'),
      },
    ] as NavItemData[]
  ).filter((item) => {
    if (item.permission) {
      return user?.access?.includes(item.permission);
    }
    return item.condition;
  });

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Logo sx={{ mt: 3, ml: 4, mb: 5 }} />
      <Stack component="nav" spacing={0.5} sx={{ px: 2 }}>
        {navConfig.map((item) =>
          item ? <NavItem key={item.title} item={item} closeNav={() => setOpenNav(false)} /> : null
        )}
      </Stack>
      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    user && (
      <Box
        sx={{
          flexShrink: { lg: 0 },
          width: { xl: NAV.WIDTH },
        }}
      >
        {upLg ? (
          <Box
            sx={{
              height: 1,
              position: 'fixed',
              width: NAV.WIDTH,
              borderRight: (theme) => `solid 1px ${theme.palette.divider}`,
            }}
          >
            {renderContent}
          </Box>
        ) : (
          <>
            <GridMenuIcon
              style={{ position: 'absolute', top: 10, left: 10, zIndex: 99 }}
              onClick={() => setOpenNav(true)}
            />
            <SwipeableDrawer
              disablePortal
              open={openNav}
              anchor="left"
              disableSwipeToOpen={!user?.is_onboarding_finished}
              onClose={() => setOpenNav(false)}
              onOpen={() => setOpenNav(true)}
              slotProps={{
                paper: {
                  sx: {
                    width: NAV.WIDTH,
                  },
                },
              }}
            >
              {renderContent}
            </SwipeableDrawer>
          </>
        )}

        <Dialog open={logout} onClose={handleCloseLogout}>
          <Close sx={{ position: 'absolute', right: 10, top: 10 }} onClick={handleCloseLogout} />
          <Stack sx={{ p: 5, alignItems: 'center', gap: 2 }}>
            <Typography>Are you sure you want to log out?</Typography>
            <Button
              variant="contained"
              onClick={() => setAuth(null)}
              sx={{
                backgroundColor: '#CE153F',
                borderRadius: 3,
                whiteSpace: 'nowrap',
                px: 4,
                py: 1,
              }}
            >
              I&apos;m sure
            </Button>
          </Stack>
        </Dialog>
      </Box>
    )
  );
}

// ----------------------------------------------------------------------

type NavItemProps = {
  item: NavItemData;
  closeNav: () => void;
};

function NavItem({ item, closeNav }: NavItemProps) {
  const pathname = usePathname();
  const { t } = useTranslation();

  const active = pathname === item.path;

  return (
    <ListItemButton
      component={RouterLink}
      href={item.path}
      onClick={item?.action || closeNav}
      sx={{
        minHeight: 44,
        borderRadius: 0.75,
        typography: 'body2',
        color: 'text.secondary',
        textTransform: 'capitalize',
        fontWeight: 'fontWeightMedium',
        ...(active && {
          color: 'primary.main',
          fontWeight: 'fontWeightSemiBold',
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          '&:hover': {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
          },
        }),
      }}
    >
      <Box component="span" sx={{ width: 24, height: 24, mr: 2 }}>
        {item.icon}
      </Box>

      <Box component="span">{t(item.title)}</Box>
    </ListItemButton>
  );
}
